/**
 * Regel-AST und Matchvertrag. Version 1 (23.10.2026).
 *
 * Grundlage: docs/REGELN_BESTAND.md (Codex, 23.10.2026). Dieses Modul legt nur die
 * Form fest; die Auswertung liegt in packages/rules (Codex). Unbekannte Konstrukte
 * werden beim Import als `unsupported` mit Fundstelle erfasst und nicht still
 * übergangen.
 */
import { z } from 'zod';
import { EbayItemId, EbaySiteId, IsoDateTime, TenantId, Version } from './entities.js';

// ---------------------------------------------------------------------------
// Normale Regeln (link_rule)
// ---------------------------------------------------------------------------

export const NormalRuleKind = z.enum([
  'same-store-category',
  'same-store-main-category',
  'same-ebay-category',
  'same-ebay-main-category',
  'fixed-store-category',
  'fixed-ebay-category',
  'title-search',
  'group', // Quellgruppe → Zielgruppe ("Individuelle/Erweiterte Gruppen")
  /** Legacy link_rule_cat 20: die PATSEARCH-Wörter des Ausgangsangebots sind der Zielsuchausdruck (Import Teil 2, 24.10.2026). */
  'pat-search',
  /** Text UND Kategorie in einer Zeile: `source`/`target` plus `categoryIds` müssen beide passen (Import Teil 2). */
  'search-and-category',
]);

/** Suchausdruck wie im Altsystem: Zeilen sind ODER, Terme einer Zeile UND. */
export const SearchTerm = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('word'), value: z.string().min(1).max(100) }),
  z.object({ kind: z.literal('phrase'), value: z.string().min(1).max(200) }),
  z.object({ kind: z.literal('not-word'), value: z.string().min(1).max(100) }),
  z.object({ kind: z.literal('item-ids'), ids: z.array(EbayItemId).min(1).max(500) }),
  z.object({ kind: z.literal('all') }), // leere Seite, bewusst entschieden
  z.object({ kind: z.literal('unsupported'), raw: z.string().max(2000), reason: z.string().max(200) }),
]);
export const SearchAlternative = z.object({ terms: z.array(SearchTerm).min(1).max(50) });
export const SearchExpr = z.object({ alternatives: z.array(SearchAlternative).min(1).max(500) });
export type SearchExpr = z.infer<typeof SearchExpr>;

/**
 * Kategoriebedingung einer Regelzeile (Legacy link_rule_link/link_rule_product: category_id +
 * category_deep bzw. shop_id + shop_deep, 24.10.2026). depth -1 = die Blattkategorie des Angebots
 * ist genau `id`; depth 1..4 = der Vorfahr des Angebots auf dieser Ebene (Legacy item_cat_x_<deep>,
 * item_shop_x_<deep>) ist `id`. `slot` wählt die erste/zweite Kategorie des Angebots, fehlt = beide.
 */
export const RuleCategoryCondition = z.object({
  family: z.enum(['ebay', 'store']),
  id: z.string().max(20),
  depth: z.union([z.literal(-1), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  slot: z.enum(['primary', 'secondary']).optional(),
});
export type RuleCategoryCondition = z.infer<typeof RuleCategoryCondition>;

export const NormalRule = z.object({
  ruleId: z.string().min(1).max(40),
  kind: NormalRuleKind,
  /** 1 = höchste; Altsystem: 1 (2.159), 2 (131), 3 (18), 0 (2). */
  priority: z.number().int().min(0).max(9).default(1),
  /** Für fixed-*: Zielkategorie-IDs. */
  categoryIds: z.array(z.string().max(20)).max(50).optional(),
  /** Für fixed-*: Regel gilt nur, wenn das Ausgangsangebot in einer dieser Kategorien liegt (Legacy-Quellkategorie). Fehlt = alle Quellen. */
  sourceCategoryIds: z.array(z.string().max(20)).max(50).optional(),
  /** Für Shopkategorie-Regeln: welche der zwei Shopkategorien des Angebots gemeint ist. Fehlt = 'any'. */
  storeSlot: z.enum(['primary', 'secondary', 'any']).optional(),
  /**
   * 'either': die Regel wird zweimal ausgewertet, A→B und B→A, mit vertauschten Rollen. Jeder
   * Ausdruck behält seine Rollensemantik: auf der Quellrolle sind Negationen und leere ODER-Arme
   * wie im Altsystem wirkungslos, auf der Zielrolle bleiben sie wirksam (Codex 24.10.2026).
   * Fehlt = 'source-to-target'.
   */
  direction: z.enum(['source-to-target', 'either']).optional(),
  /**
   * Korrelierte Zeilenbedingungen (Legacy: query UND category UND shop in einer Zeile): alle
   * gesetzten Bedingungen müssen gleichzeitig gelten. `sourceCategory` bindet das Ausgangsangebot,
   * `targetCategory` jedes Zielangebot; `depth` siehe RuleCategoryCondition. Für `same-*`-Arten gibt
   * `sameDepth` die Ebene an, auf der Quelle und Ziel übereinstimmen müssen (fehlt = Art-Standard:
   * same-*-category -1, same-*-main-category 1). Nicht darstellbare Legacy-Zeilen bleiben `unsupported`.
   */
  sourceCategory: RuleCategoryCondition.optional(),
  targetCategory: RuleCategoryCondition.optional(),
  sameDepth: z.union([z.literal(-1), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
  /** Für title-search und group: Quell- und Zielausdruck. */
  source: SearchExpr.optional(),
  target: SearchExpr.optional(),
  legacyRuleId: z.string().optional(),
});
export type NormalRule = z.infer<typeof NormalRule>;

// ---------------------------------------------------------------------------
// Skriptregeln (manager_script) als AST
// ---------------------------------------------------------------------------

/**
 * Richtungen laut Legacy-Parser:
 * - 'source-to-target'  `A <= B`: passt Ausgang zu A, zeige B
 * - 'target-to-source'  `A => B`: passt Ausgang zu B, zeige A
 * - 'either'            `A <=> B`: erst A→B, nur sonst B→A (ELSEIF, keine Vereinigung)
 * - 'self'              `A`: A ist Quelle und Ziel
 */
export const ScriptDirection = z.enum(['source-to-target', 'target-to-source', 'either', 'self']);

export const ScriptLine = z.object({
  lineNo: z.number().int().positive(),
  direction: ScriptDirection,
  left: SearchExpr,
  right: SearchExpr.optional(),
});

/**
 * Entscheidungen zu den vier Sonderfällen aus REGELN_BESTAND.md (Claude, 23.10.2026):
 * 1. Mehrwortige Komma-Alternative `(Modul 100, Anlage 100)` wird als
 *    `(Modul UND 100) ODER (Anlage UND 100)` importiert, auf Quell- und Zielseite gleich;
 *    Wortreihenfolge ist keine Bedingung. Bewusste Korrektur der Zielsuche, mit Vergleichsfall.
 * 2. Leere Zielseite `A <=` wird als Zielausdruck `{ kind: 'all' }` importiert (Altwirkung:
 *    alle zulässigen Angebote); Sortierung, Limit, Site und Eigenausschluss gelten.
 * 3. Leere Quellseite `<= B` wird als Quellausdruck `{ kind: 'all' }` importiert.
 *    Neue Eingaben mit leerer Seite lehnt die UI ab und bietet „Alle“ ausdrücklich an.
 * 4. Zeilen mit Präfix `//` werden als Notizen in `comments` übernommen, nicht als Regeln;
 *    `*` ist nie eine Wildcard. Das ist eine bewusste Abweichung vom Altparser und wird
 *    für das betroffene Skript per Golden-Vergleich geprüft, bevor Revisionen laufen.
 */
export const ScriptComment = z.object({ lineNo: z.number().int().positive(), text: z.string().max(2000) });

export const ScriptRule = z.object({
  scriptId: z.string().min(1).max(40),
  name: z.string().max(120).optional(),
  lines: z.array(ScriptLine).max(1000),
  /** Notizzeilen des Kunden (Präfix `//`), erhalten, ohne Wirkung. Fehlt = keine. */
  comments: z.array(ScriptComment).max(1000).optional(),
  /** Zeilen, die der Importer nicht übersetzen konnte; Skript bleibt gesperrt, solange nicht leer. */
  unsupportedLines: z.array(z.object({ lineNo: z.number().int().positive(), reason: z.string().max(200) })).default([]),
  legacyScriptId: z.string().optional(),
});
export type ScriptRule = z.infer<typeof ScriptRule>;

export const RuleSet = z.object({
  astVersion: z.literal(1),
  tenantId: TenantId,
  galleryId: z.string(),
  version: Version,
  /**
   * Quellprüfung (Codex-Vergleich 23.10.2026): das Altsystem prüft die Quelle per
   * Teilstring ohne Groß-/Kleinschreibung, die neue Engine per Unicode-Wortgrenze.
   * Importierte Regeln laufen mit 'legacy-substring', damit die Auswahl der Kunden
   * unverändert bleibt; neue Regeln mit 'word'. Fehlt = 'word'.
   *
   * Zielsuche (Befund 23.10.2026, privater Vergleich Fall 2): das Altsystem sucht Ziele per MySQL
   * Boolean-FULLTEXT über `item_index_search` = Titel + item_pat_search + Angebots-ID, also
   * wortbasiert, Tokens ab drei Zeichen. Kategorie- und Shopindizes tragen präfixierte Tokens und
   * sind für Suchworte nicht erreichbar. Daher in beiden Modi: Haystack der Zielsuche = Wörter aus
   * `title` + `patSearch` + `ebayItemId`; fehlendes `patSearch` ist 'pat-search' in missingData.
   * Bestätigte Treffer zählen immer: Ein Kandidat, dessen Titel das Suchwort enthält, ist ein
   * Treffer, auch wenn sein `patSearch` oder das anderer Kandidaten fehlt. Unbekannt ist nur ein
   * Nicht-Treffer ohne `patSearch`; er macht die Auswahl unvollständig (selectionComplete=false),
   * verwirft aber nicht die Treffer der Regel (Entscheidung 23.10.2026 zu Engine 1.3).
   */
  sourceMatch: z.enum(['word', 'legacy-substring']).optional(),
  /** Reihenfolge laut Altsystem: Skripte zuerst, dann normale Regeln nach Priorität. */
  /** Altbestand: bis 92 Skripte in einer Galerie (Import Teil 2); daher 200. */
  scripts: z.array(ScriptRule).max(200),
  rules: z.array(NormalRule).max(2500),
  /** Galerie gesperrt für Neuberechnung, bis Sonderfälle geprüft sind; Gründe siehe RuleSetLockReason. */
  locked: z.object({ reason: z.string().max(200), since: IsoDateTime, kind: z.enum(['unsupported-lines', 'golden-pending', 'manual']).optional() }).optional(),
  updatedAt: IsoDateTime,
});
export type RuleSet = z.infer<typeof RuleSet>;

/**
 * Physische Ablage großer Regelwerke (X3, 24.10.2026): Passt das vollständige RuleSet nicht in
 * den DynamoDB-Datensatz (Richtwert 350 KB), speichert packages/data unter `RULES#` nur diesen
 * Header; `rules`/`scripts` liegen als JSON-Objekt (das unveränderte Domain-RuleSet) im
 * Regel-Bucket der Stage. Reihenfolge: S3-Objekt zuerst (immutabel, konditional), danach Header
 * atomar per CAS. Jeder Leser und jeder CAS-Schreiber geht über denselben Resolver in
 * packages/data; die Fachlogik sieht immer das vollständige RuleSet. Bucket/Stage/IAM stimmen
 * Codex (data/sync) und Opus-1 (App-Rolle lesend) ab.
 */
export const RuleSetStorageHeader = RuleSet.pick({ astVersion: true, tenantId: true, galleryId: true, version: true, locked: true, updatedAt: true, sourceMatch: true }).extend({
  storage: z.object({
    kind: z.literal('s3'),
    objectKey: z.string().min(1).max(512),
    versionId: z.string().max(128).optional(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    bytes: z.number().int().positive(),
  }),
});
export type RuleSetStorageHeader = z.infer<typeof RuleSetStorageHeader>;

// ---------------------------------------------------------------------------
// Matchvertrag (API-Vorschau und Worker nutzen denselben Kern)
// ---------------------------------------------------------------------------

export const MatchContext = z.object({
  tenantId: TenantId,
  /** Zielsite der Galerie (Gallery.site, Selection.site): hier werden die Zielangebote gesucht. */
  site: EbaySiteId,
  /**
   * Site des Ausgangsangebots (S7, 25.10.2026, Codex-1): fehlt sie, gilt `site`. Der tatsächliche Source-ITEM behält seine
   * Site und muss exakt zu `sourceSite` passen (keine Umetikettierung). Kategorien sind je Site identifiziert: gleich ist nur
   * das Paar (Site, Kategorie-ID), nie die Nummer allein; es gibt kein Cross-Site-Mapping. Quellkonstanten einer Regel meinen
   * den Kategoriebaum von `sourceSite`, Zielkonstanten den von `site`.
   */
  sourceSite: EbaySiteId.optional(),
  galleryId: z.string(),
  /** Ohne Ausgangsangebot nur als Layout-/Fallback-Vorschau zulässig. */
  sourceItemId: EbayItemId.optional(),
  rulesVersion: Version,
  dataVersion: z.string().max(64),
  engineVersion: z.string().max(16),
  referenceTime: IsoDateTime,
  /** Gemeinsame Regel: matchSeed(galleryId, sourceItemId, rulesVersion, dataVersion); Vorschau und Worker identisch. */
  seed: z.string().max(128),
  maxItems: z.number().int().min(1).max(100),
  /** Ungespeicherter Entwurf in der Vorschau: rulesVersion = 0 und Hash des Entwurfs. */
  draftHash: z.string().regex(/^[a-f0-9]{16,64}$/).optional(),
  /** Version der Galerieeinstellungen, die in Hash und Einbettcode eingehen. */
  galleryVersion: Version.optional(),
});
export type MatchContext = z.infer<typeof MatchContext>;

/** Site des Ausgangsangebots: `sourceSite`, sonst die Zielsite (S7). */
export function matchSourceSite(context: { site: number; sourceSite?: number | undefined }): number {
  return context.sourceSite ?? context.site;
}
/** Kategorie-Identität je Site (S7): gleich nur bei gleicher Site und gleicher ID. */
export function sameCategory(a: { site: number; categoryId: string }, b: { site: number; categoryId: string }): boolean {
  return a.site === b.site && a.categoryId === b.categoryId;
}
/** Deterministischer Seed, identisch in Vorschau und Worker (Opus-1, Stufe D). */
export function matchSeed(galleryId: string, sourceItemId: string | undefined, rulesVersion: number, dataVersion: string): string {
  return `${galleryId}:${sourceItemId ?? '-'}:${rulesVersion}:${dataVersion}`;
}

/**
 * Lebenszyklus von RuleSet.locked: setzt der Importer, wenn unsupportedLines nicht leer
 * sind oder ein Sonderfall einen Golden-Vergleich verlangt; der Worker überspringt
 * gesperrte Galerien. Entscheidung 23.10.2026 (Opus-1-Anfrage): `locked` bestimmt beim
 * Speichern ausschließlich `packages/data` (Codex), die App zeigt es nur an. Regel:
 * Speichern mit verbleibenden nicht übersetzbaren Altinhalten ist erlaubt, `unsupported-lines`
 * bleibt, solange solche Inhalte enthalten sind; `golden-pending` bleibt, solange importierte
 * Skripte oder importierte Regeln enthalten sind, und fällt erst weg, wenn der Kunde sie
 * vollständig durch eigene Regeln ersetzt hat; `manual` hebt ein Speichern im Editor auf.
 * Claude hebt `golden-pending` nach bestandenem Vergleich über den Betriebspfad auf; der
 * Worker hebt nie auf.
 */
export const RuleSetLockReason = z.enum(['unsupported-lines', 'golden-pending', 'manual']);

export const MatchResult = z.object({
  context: MatchContext,
  orderedItemIds: z.array(EbayItemId).max(100),
  appliedRules: z.array(z.object({ ruleId: z.string(), kind: z.string(), hits: z.number().int().nonnegative() })),
  /** Erklärung je Treffer: welche Regel hat das Angebot gewählt (erste zutreffende). */
  explanations: z.array(z.object({ itemId: EbayItemId, ruleId: z.string() })).max(100).optional(),
  excludedSelf: z.boolean(),
  usedFallback: z.boolean(),
  /** Zählung: `complete` nur, wenn die Trefferzählung vor dem Limit vollständig berechnet wurde. Betrifft Anzeige/Diagnostik, nicht die Freigabe. */
  completeness: z.enum(['complete', 'partial', 'unknown']),
  /**
   * Auswahl (Codex 23.10.2026): true, wenn `orderedItemIds` bis zum Limit vollständig bestimmt ist,
   * also jede Regel über alle Kandidaten lief, die sie brauchte, `missingData` leer ist und die
   * beschlossene Kandidatengrenze der Zufallssuche eingehalten wurde. `completeness = 'partial'`
   * schließt das nicht aus. Nur dieser Wert geht als `Selection.complete` in den Description-Worker.
   * Fehlt = false. Ab v0.2 Pflicht.
   */
  selectionComplete: z.boolean().optional(),
  missingData: z.array(z.enum(['category-hierarchy', 'store-hierarchy', 'second-category', 'title', 'pat-search'])).default([]),
  selectionHash: z.string().regex(/^[a-f0-9]{64}$/),
});
export type MatchResult = z.infer<typeof MatchResult>;
